import React, { useState, useEffect } from 'react'
import { prefixLocale, getLink } from '@helpers/env'
import { SizingWidgetData } from '@helpers/products/sizingWidgetData'
import Translations from '@helpers/translations'
import { ReactComponent as CloseIcon } from '@svgs/keyline/close.svg'
import SizingWidget from '@components/product/SizingWidget'
import internationalTableData from './size_chart_international.json'
import SizeChartHeader from './SizeChartHeader'
import SizeChartDropDown from './SizeChartDropDown'
import SizeChartTableSelector from './SIzeChartTableSelector'
import HowToMeasure from './HowToMeasure'
import styles from './styles.module.scss'
import { EmptyObject } from 'types/helpers'
import { NextRouter } from 'next/router'
import { getSizeChart } from '@api/sizeChart'
import { SIZE_CHART_TOP_SORT_ORDER } from '@consts/index'

type SizeChart = {
  items: {
    build: string | null
    cut: string | null
    leg: string | null
    product_measurement_type: string
    product_type: string
    measurements: { key: string; value: number }[]
  }[]
}

type RouterQueryProps = {
  pagesHandle: string
  type?: string
  preview?: string
}

const TABS_MAP = {
  a: Translations.localise('pages.size-chart.tabs.build-a'),
  b: Translations.localise('pages.size-chart.tabs.build-b'),
  c: Translations.localise('pages.size-chart.tabs.build-c'),
  slim: Translations.localise('pages.size-chart.tabs.slim'),
  straight: Translations.localise('pages.size-chart.tabs.straight'),
  dadletic: Translations.localise('pages.size-chart.tabs.dadletic'),
  sizes: Translations.localise('pages.size-chart.tabs.sizes')
} as const

const SIZE_CHART_COLLECTION = {
  trousers: {
    heroes: 'Heroes',
    'winter-heroes': 'Winter Heroes',
    'summer-sharps': 'Summer Sharps',
    'linen-sharps': 'Linen Sharps',
    sharps: 'Sharps',
    'cord-sharps': 'Cord Sharps',
    'enduro-tech-sharps': 'Enduro Tech Sharps',
    'enduro-fives': 'Enduro Fives',
    fives: 'Fives',
    'cord-fives': 'Cord Fives',
    bulletproofs: 'Bulletproofs',
    smarts: 'Smarts',
    'summer-smarts': 'Summer Smarts',
    'winter-smarts': 'Winter Smarts',
    nomads: 'Nomads',
    'house-trouser': 'House Trouser',
    hackneys: 'Hackneys',
    'cord-hackneys': 'Cord Hackneys',
    'linen-fridays': 'Linen Fridays',
    'nantucket-fives': 'Nantucket Fives',
    condors: 'Condors',
    'heritage-heroes': 'Heritage Heroes',
    'travel-fives': 'Travel Fives',
    'moleskin-fives': 'Moleskin Fives',
    'cashmere-sharps': 'Cashmere Sharps',
    'winter-sharps': 'Winter Sharps',
    'velvet-sharps': 'Velvet Sharps',
    'seersucker-nomads': 'Seersucker Nomads',
    cargos: 'Cargos'
  },
  denim: {
    'original-denim': 'Original Denim',
    'travel-denim': 'Travel Denim',
    'selvedge-denim': 'Selvedge Denim',
    'italian-denim': 'Italian Denim',
    'japanese-denim': 'Japanese Denim',
    'hemp-denim': 'Hemp Denim',
    'denim-heroes': 'Denim Heroes',
    'stonewash-denim': 'Stonewash Denim'
  },
  shorts: {
    'condor-shorts': 'Condor Shorts',
    'sharp-shorts': 'Sharp Shorts',
    'milano-shorts': 'Milano Shorts',
    'friday-shorts': 'Friday Shorts',
    'hero-shorts': 'Hero Shorts',
    'seersucker-shorts': 'Seersucker Shorts',
    swims: 'Swims',
    'action-swims': 'Action Swims',
    'cord-nomad-shorts': 'Cord Nomad Shorts',
    'nomad-shorts': 'Nomad Shorts',
    'house-shorts': 'House Shorts'
  },
  tops: {
    'organic-pique-polo': 'Organic Pique Polo',
    tolo: 'Tolo',
    'towelling-polo': 'Towelling Polo',
    'original-tee': 'Original Tee',
    'long-sleeve-tee': 'Long Sleeve Tee',
    'enduro-shirt': 'Enduro Shirt',
    'enduro-tee': 'Enduro Tee',
    'merino-polo': 'Merino Polo',
    'merino-crew': 'Merino Crew',
    'merino-half-zip': 'Merino Half Zip',
    'cord-shirt': 'Cord Shirt',
    'denim-shirt': 'Denim Shirt',
    'linen-shirt': 'Linen Shirt',
    'cotton-oxford': 'Cotton Oxford',
    sweatshirt: 'Sweatshirt',
    'cashmere-half-zip': 'Cashmere Half Zip',
    overshirt: 'Overshirt',
    'the-jack': 'The Jack',
    'button-through-polo': 'Button-Through Polo',
    'slub-tee': 'Slub Tee',
    'lightweight-sweatshirt': 'Lightweight Sweatshirt',
    'condor-polo': 'Condor Polo',
    'flannel-shirt': 'Flannel Shirt',
    'seersucker-cuban': 'Seersucker Cuban',
    'heavyweight-tee': 'Heavyweight Tee'
  },
  accessories: {
    'leather-belts': 'Leather Belts',
    'woven-belts': 'Woven Belts',
    'house-socks': 'House Socks'
  },
  underwear: {
    trunks: 'Trunks'
  }
} as const

function getProductBuild(
  selectedProductSizeChart: SizeChart | undefined
): string {
  const defaultBuildType = 'a'
  if (!selectedProductSizeChart) {
    return defaultBuildType
  }

  return (
    selectedProductSizeChart?.items[0]?.build ||
    selectedProductSizeChart?.items[0]?.cut ||
    selectedProductSizeChart?.items[0]?.leg ||
    defaultBuildType
  )
}

function getParam(
  param: string,
  router: NextRouter,
  pagesHandle: string,
  selectedSizeChart?: SizeChart
) {
  const path = router.asPath.split('?')
  const index = pagesHandle.indexOf('-size-chart')
  const productCategory = index > -1 ? pagesHandle.slice(0, index) : 'trousers'
  const productType =
    path.length > 1 && path[1].includes('type')
      ? decodeURI(path[1].split('=')[1])
      : selectedSizeChart?.items[0]?.product_type || 'heroes'

  const buildType = getProductBuild(selectedSizeChart)

  if (param === 'category') {
    return productCategory
  }

  if (param === 'product') {
    return productType
  }

  if (param === 'build') {
    return buildType
  }

  return undefined
}

function getInternationalTableRows(
  productCategory: string | undefined,
  selectedProduct: string
) {
  const internationalTableByCategory =
    internationalTableData[
      productCategory as keyof typeof internationalTableData
    ]

  switch (productCategory) {
    case 'tops':
      return (
        internationalTableByCategory?.[
          selectedProduct as keyof typeof internationalTableByCategory
        ]?.table || internationalTableByCategory?.default?.table
      )

    case 'accessories':
      if (/socks/.test(selectedProduct)) {
        const socksTable =
          internationalTableData['socks' as keyof typeof internationalTableData]
            ?.default?.table
        if (socksTable) return socksTable
      }
      break

    default:
      break
  }

  return internationalTableData.trousers?.default.table || []
}

interface SizeChartTableStructure {
  tableColumns: string[]
  tableRows: string[][]
  tableTabs:
    | {
        [key: string]: string
      }
    | EmptyObject
}

function getSizeChartTableStructure(
  selectedProductSizeChart: SizeChart | undefined,
  selectedBuildType: string
): SizeChartTableStructure {
  const emptyTable = {
    tableColumns: [],
    tableRows: [],
    tableTabs: {}
  }

  try {
    if (!selectedProductSizeChart) return emptyTable

    const sizeChartItems = selectedProductSizeChart.items.filter(
      ({ build, cut, leg }) => {
        return [
          build?.toLowerCase(),
          cut?.toLowerCase(),
          leg?.toLowerCase()
        ].includes(selectedBuildType?.toLowerCase())
      }
    )

    if (sizeChartItems.length === 0) return emptyTable

    const tableColumns = sizeChartItems[0].measurements
      .map(({ key }) => key)
      .sort((a, b) => a.localeCompare(b))
      .sort(
        (a, b) =>
          SIZE_CHART_TOP_SORT_ORDER.indexOf(a) -
          SIZE_CHART_TOP_SORT_ORDER.indexOf(b)
      )

    const tableRows = sizeChartItems.map(
      ({ measurements, product_measurement_type }) => {
        const values = measurements
          .sort((a, b) => a.key.localeCompare(b.key))
          .sort(
            (a, b) =>
              SIZE_CHART_TOP_SORT_ORDER.indexOf(a.key) -
              SIZE_CHART_TOP_SORT_ORDER.indexOf(b.key)
          )
          .map(({ value }) => value.toString())

        return [product_measurement_type, ...values]
      }
    )

    const tabs: Record<string, string> = {}

    const tabsOrder = Object.keys(TABS_MAP)
    const measurementTabs = selectedProductSizeChart.items
      .map(({ build, cut, leg }) => (build || cut || leg)?.toLowerCase())
      .filter(Boolean)
      .sort((a, b) => tabsOrder.indexOf(a) - tabsOrder.indexOf(b))

    new Set(measurementTabs).forEach(key => {
      if (key && TABS_MAP[key as keyof typeof TABS_MAP]) {
        tabs[key] = TABS_MAP[key as keyof typeof TABS_MAP]
      }
    })

    return {
      tableColumns: ['Spoke Size', ...tableColumns],
      tableRows,
      tableTabs: tabs
    }
  } catch (err) {
    console.error(err)
    return emptyTable
  }
}

const SizeChart = ({
  router,
  pagesHandle,
  sizingWidgetData
}: {
  router: NextRouter
  pagesHandle: string
  sizingWidgetData?: SizingWidgetData | EmptyObject
}) => {
  const { type: productType, preview } = router.query as RouterQueryProps
  const [selectedSizeChart, setSelectedSizeChart] = useState<SizeChart>()
  const [productCategory, setProductCategory] = useState(
    getParam('category', router, pagesHandle, selectedSizeChart) || 'trousers'
  )
  const [selectedBuildType, setSelectedBuildType] = useState('')
  const [selectedProduct, setSelectedProduct] = useState('')

  const selectedSizeChartCollection =
    productCategory && productCategory in SIZE_CHART_COLLECTION
      ? SIZE_CHART_COLLECTION[
          productCategory.toLowerCase() as keyof typeof SIZE_CHART_COLLECTION
        ]
      : {}

  const listOfProductTypes = Object.keys(selectedSizeChartCollection)

  const internationalTableRows = getInternationalTableRows(
    productCategory,
    selectedProduct
  )

  const { tableColumns, tableRows, tableTabs } = getSizeChartTableStructure(
    selectedSizeChart,
    selectedBuildType
  )

  const handleSetProductCategory = (category: string) => {
    const firstProductOfCategory = Object.keys(
      SIZE_CHART_COLLECTION[category as keyof typeof SIZE_CHART_COLLECTION]
    ).sort((a, b) => a.localeCompare(b))[0]

    const buildType = getProductBuild(selectedSizeChart)
    setSelectedBuildType(buildType)
    setSelectedProduct(firstProductOfCategory)
    setProductCategory(category)
  }

  const handleSetSelectedProduct = (productType: string) => {
    const buildType = getProductBuild(selectedSizeChart)
    setSelectedBuildType(buildType)
    setSelectedProduct(productType)
  }

  const updateSizeChart = async (productType: string) => {
    const { sizeChart } = await getSizeChart(productType)
    setSelectedSizeChart(sizeChart)
    setSelectedBuildType(
      getParam('build', router, pagesHandle, sizeChart) || ''
    )
  }

  useEffect(() => {
    const product = getParam('product', router, pagesHandle)
    if (product) {
      setSelectedProduct(product)
      updateSizeChart(product)
    }
  }, [router, pagesHandle])

  useEffect(() => {
    if (!!selectedProduct) {
      const url = `${prefixLocale()}/pages/${productCategory}-size-chart${`?type=${selectedProduct}`}`
      window.history.replaceState(null, '', url)
      updateSizeChart(selectedProduct)
    }
  }, [productCategory, selectedProduct])

  const goBack = () => {
    if (
      document.referrer.includes(window.location.hostname) &&
      document.referrer !== window.location.href
    ) {
      window.location.href = document.referrer
      return
    }
    window.location.href = getLink('/')
  }

  return (
    <>
      <div className={styles.sizeChart}>
        <div className={styles.sizeChart__container}>
          <div className={styles.sizeChart__wrapper}>
            <button
              type="button"
              className={styles.sizeChart__close}
              onClick={goBack}
            >
              <CloseIcon />
            </button>
            <SizeChartHeader
              options={Object.keys(SIZE_CHART_COLLECTION)}
              productCategory={productCategory}
              setProductCategory={handleSetProductCategory}
            />
            <div
              className={`${styles.sizeChart__dropdown} ${
                listOfProductTypes.length > 1
                  ? styles.sizeChart__dropdown_visible
                  : ''
              }`}
            >
              <SizeChartDropDown
                list={listOfProductTypes}
                collection={
                  SIZE_CHART_COLLECTION[
                    productCategory?.toLowerCase() as keyof typeof SIZE_CHART_COLLECTION
                  ]
                }
                selectedProduct={selectedProduct}
                setSelectedProduct={handleSetSelectedProduct}
              />
            </div>
            <SizeChartTableSelector
              tabs={tableTabs}
              setSelectedBuildType={setSelectedBuildType}
              selectedBuildType={selectedBuildType}
              sizeChartColumn={tableColumns || []}
              sizeChartBuild={tableRows}
              productCategory={productCategory}
              productType={productType}
              internationalTableRows={internationalTableRows}
            />
          </div>
        </div>
      </div>
      {!!sizingWidgetData && (
        <SizingWidget
          sizingWidgetData={sizingWidgetData}
          productType={productType}
          productCategory={productCategory}
        />
      )}

      <HowToMeasure />
    </>
  )
}

export default SizeChart
